import React, {useMemo} from "react";
import {connect} from "react-redux";
import {toDate} from "date-fns";
import {DATE_FORMAT, styled} from "@reside/ui";

import {
  AdmissionId,
  CellFooter,
  CellIdentityBlock,
  CellLastUpdatePrefix,
  CellLastUpdateRow,
  CellLastUpdateTime,
  CellResidentNameLink,
  DashboardCell,
  Left,
  Right,
} from "../../../../dashboard-cell";
import {WorkflowRibbon} from "../../../../dashboard-cell/WorkflowRibbon";
import {AdmissionFromSearch} from "../../../../../services/AdmissionsService";
import {RelativeDateTime} from "../../../../relative-date-time";
import {PdfUploadStatusIcon} from "../../pdf-upload-status-icon";
import {getAdmissionResidentName, getResidentTestId} from "../../../helper";
import {AdmissionCardActionsMenu} from "../AdmissionCardActionsMenu";

import {AdmissionChecklist} from "../admission-checklist";
import {DownloadPDFDropdownItem} from "../DownloadPDFDropdownItem";
import {DownloadArbitrationPDFDropdownItem} from "../DownloadArbitrationPDFDropdownItem";
import {DownloadPartialPDFDropdownItem} from "./DownloadPartialPDFDropdownItem";
import {DeleteAdmissionDropdownItem} from "../DeleteAdmissionDropdownItem";
import {ViewAdmissionDropdownItem} from "../ViewAdmissionDropdownItem";
import {ArchiveAdmissionFooterButton} from "./ArchiveAdmissionDropdownItem";
import {PacketSizeLimitAlertIcon} from "../../packet-size-limit-alert-icon/PacketSizeLimitAlertIcon";
import {select} from "../../../../../store";
import {shouldShowPacketSizeLimitAlert} from "./helpers";
import {StarAdmissionIcon} from "../../../../star-icon/StarAdmissionIcon";
import {ForceDeleteAdmissionDropdownItem} from "../ForceDeleteAdmissionDropdownItem";
import {getAdmissionViewHref} from "../../../../dashboard-cell/admissionRoutes";

type Props = Readonly<{
  admission: AdmissionFromSearch;
  hasShowLargeAdmissionWarningFlag?: boolean;
  isResideAdmin: boolean;
}>;

export const AdmissionApprovedCellRenderer = ({
  admission,
  hasShowLargeAdmissionWarningFlag,
  isResideAdmin,
}: Props) => {
  const showPacketSizeLimitAlert = useMemo(
    () =>
      shouldShowPacketSizeLimitAlert(
        admission,
        isResideAdmin,
        hasShowLargeAdmissionWarningFlag,
      ),
    [admission, hasShowLargeAdmissionWarningFlag, isResideAdmin],
  );
  const updatedAt = toDate(admission.updated * 1000);

  return (
    <DashboardCell
      withWarningBorder={showPacketSizeLimitAlert}
      admissionHref={getAdmissionViewHref(admission.id)}
    >
      <WorkflowRibbon
        workflowType={admission.workflowType}
        star={
          <StarAdmissionIcon
            admissionId={admission.id}
            isStarred={admission.starred}
          />
        }
        actions={
          <AdmissionCardActionsMenu
            testId={getResidentTestId(admission, "approvedCardActions")}
          >
            <ViewAdmissionDropdownItem admission={admission} />
            <DownloadPDFDropdownItem admission={admission} />
            <DownloadArbitrationPDFDropdownItem admission={admission} />
            <DownloadPartialPDFDropdownItem admission={admission} />
            <DeleteAdmissionDropdownItem admission={admission} />
            <ForceDeleteAdmissionDropdownItem admission={admission} />
          </AdmissionCardActionsMenu>
        }
      />
      <CellIdentityBlock>
        <IdentityRow>
          <div>
            <CellResidentNameLink
              name={getAdmissionResidentName(admission)}
              residentId={admission.residentId}
              data-test-id={getResidentTestId(admission, "editProfile")}
            />
            <AdmissionId code={admission.code} />
          </div>
          <IdentityIcons>
            {showPacketSizeLimitAlert ? <PacketSizeLimitAlertIcon /> : null}
            {admission.resources?.finalPdf?.pccUploadStatus ? (
              <PdfUploadStatusIcon
                pdfUploadStatus={admission.resources.finalPdf.pccUploadStatus}
              />
            ) : null}
          </IdentityIcons>
        </IdentityRow>
      </CellIdentityBlock>
      <AdmissionChecklist admission={admission} />
      <CellFooter>
        <Left>
          <CellLastUpdateRow>
            <CellLastUpdatePrefix>Last update:</CellLastUpdatePrefix>
            <CellLastUpdateTime>
              <RelativeDateTime
                dateTime={updatedAt}
                dateFormat={DATE_FORMAT.DAY_IN_MONTH}
              />
            </CellLastUpdateTime>
          </CellLastUpdateRow>
        </Left>
        <Right>
          <ArchiveAdmissionFooterButton admission={admission} />
        </Right>
      </CellFooter>
    </DashboardCell>
  );
};

const mapState = (state: any) => ({
  hasShowLargeAdmissionWarningFlag:
    select.adminSession.hasShowLargeAdmissionWarningFlag(state),
  isResideAdmin: select.adminSession.isResideAdmin(state),
});

export const AdmissionApprovedCell = connect(mapState)(
  AdmissionApprovedCellRenderer,
);

const IdentityRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 8px;
`;

const IdentityIcons = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  padding-top: 2px;
`;
